import { MetricIdentifier } from 'modules/experiments/constants';
import {
  getExperimentId,
  getExperimentName,
  getExperimentTranslations,
} from 'modules/experiments/ducks/selectors';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import {
  ExperimentVariantBreakdownArray,
  MetricOption,
  TABLE_EXPORT,
} from 'modules/experiments/types';
import {
  getTableDefaultAndOptionalExportHeaders,
  getTableDisplayHeaders,
  getTableExportData,
  getTableExportingActions,
  getTableExportingOptions,
} from 'modules/experiments/utils';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Export24 } from 'ui/icons';
import { Menu } from 'ui/menu';
import { ALIGNMENT } from 'ui/selects';

type ExportButtonProps = {
  rows: ExperimentVariantBreakdownArray;
  metricMap: Record<string, MetricIdentifier>;
  selectedMetricsColumn: string[];
  metricOptions: MetricOption<string>[];
};

export const ExportButton = ({
  rows,
  metricMap,
  selectedMetricsColumn,
  metricOptions,
}: ExportButtonProps) => {
  const { t } = useExperimentsTranslations();
  const translations = useSelector(getExperimentTranslations);
  const experimentId = useSelector(getExperimentId);
  const experimentName = useSelector(getExperimentName);
  const options = getTableExportingOptions(t);
  const translationObj = {
    ...translations.monitor.export.defaultHeaders,
    ...translations.experimentType,
  };

  // List of column names segregated as default for all exports and optional
  const { defaultHeaders, optionalHeaders } = getTableDefaultAndOptionalExportHeaders(
    translationObj,
    metricOptions,
  );

  // List of column names segregated for current table view and all metrics export view
  const { allMetricsHeaders, currentViewHeaders } = getTableDisplayHeaders({
    defaultHeaders,
    optionalHeaders,
    selectedMetricsColumn,
  });

  const csvData = useMemo(
    () =>
      getTableExportData({
        rows,
        metricMap,
        metricOptions,
        translations,
      }),
    [rows.length],
  );

  const exportActions = getTableExportingActions({
    experimentId,
    experimentName,
    csvData,
    allMetricsHeaders,
    currentViewHeaders,
  });

  return (
    <Menu
      data-test="variant-breakdown-header-export-actions"
      horizontalAlignment={ALIGNMENT.RIGHT}
      handleSelectionChange={(key: string) => exportActions[key as TABLE_EXPORT]()}
      options={options}
    >
      <IconButton variant={BUTTON_VARIANTS.SECONDARY} size={BUTTON_SIZES.LARGE}>
        <Export24 />
      </IconButton>
    </Menu>
  );
};
